import { AgCartesianChartOptions, AgChartOptions } from "ag-charts-community";

import {
  ANALYTICS_CUSTOM_PRIMARY_COLOR,
  ANALYTICS_CUSTOM_SERIES_COLORS,
  CHART_LINE_MARKER_SIZE,
  CHART_LINE_STROKE_WIDTH,
  CHART_SERIES_STROKE_WIDTH_NONE,
  CHART_SMOOTH_TENSION,
  MODEL_BAR_CORNER_RADIUS,
  analyticsChartColors,
} from "../constants";
import type { DateRangeEntry } from "../types";
import { buildBucketAlignedRange } from "../utils/createDateRangeSelectionHandler";
import { createAreaGradientFill } from "../utils/createAreaGradientFill";
import {
  type CustomChartOptionsContext,
  prepareMultiSeriesRows,
  prepareSingleSeriesRows,
} from "../utils/customChartDataPrep";
import { isRecord } from "../utils/isRecord";

import { baseAnalyticsChartOptions } from "./base";
import { bottomCategoryAxis, buildBottomDateAxis, numberAxis } from "./axes";
import {
  buildDerivedInsightRows,
  createAnalyticsTooltipHtml,
  formatAxisValue,
  type AnalyticsTooltipRow,
} from "./customChartFormatters";
import { buildDateChartSelectionOptions } from "./selection";
import { buildHeatmapCustomChartOptions } from "./heatmapCustomChart";
import { buildHierarchyCustomChartOptions } from "./hierarchyCustomChart";
import { buildHistogramCustomChartOptions } from "./histogramCustomChart";
import { buildPolarCustomChartOptions } from "./polarCustomChart";

import {
  CustomChartType,
  type CustomChartSeriesMeta,
  type CustomChartResponseItem,
} from "@/queries/analytics-custom-charts-types";

export type { CustomChartOptionsContext } from "../utils/customChartDataPrep";

interface CartesianSeriesContext {
  color: string;
  label: string;
  tooltip: {
    renderer: ({ datum }: { datum: Record<string, unknown> }) => string;
  };
  xKey: string;
  yKey: string;
}

const buildBarSeries = ({ color, label, tooltip, xKey, yKey }: CartesianSeriesContext) => ({
  type: "bar" as const,
  xKey,
  yKey,
  yName: label,
  fill: color,
  strokeWidth: CHART_SERIES_STROKE_WIDTH_NONE,
  cornerRadius: MODEL_BAR_CORNER_RADIUS,
  tooltip,
});

const buildLineOrAreaSeries = (
  chartType: CustomChartType,
  { color, label, tooltip, xKey, yKey }: CartesianSeriesContext,
) => {
  const shared = {
    xKey,
    yKey,
    yName: label,
    stroke: color,
    strokeWidth: CHART_LINE_STROKE_WIDTH,
    marker: {
      enabled: true,
      fill: color,
      stroke: color,
      size: CHART_LINE_MARKER_SIZE,
    },
    interpolation: { type: "smooth" as const, tension: CHART_SMOOTH_TENSION },
    tooltip,
  };

  switch (chartType) {
    case CustomChartType.Area:
      return {
        type: "area" as const,
        ...shared,
        fill: createAreaGradientFill(color),
        connectMissingData: true,
      };
    case CustomChartType.Line:
    default:
      return {
        type: "line" as const,
        ...shared,
      };
  }
};

const buildCartesianSeries = (chartType: CustomChartType, context: CartesianSeriesContext) => {
  switch (chartType) {
    case CustomChartType.Bar:
      return buildBarSeries(context);
    case CustomChartType.Area:
    case CustomChartType.Line:
    default:
      return buildLineOrAreaSeries(chartType, context);
  }
};

const isMultiSeriesChart = (chart: CustomChartResponseItem) =>
  chart.series.length > 1 || (chart.series[0]?.key !== "value" && chart.series.length === 1);

const getTraceStatusSeriesColor = (series: CustomChartSeriesMeta, index: number) => {
  const status = String(series.rawLabel ?? series.label).toLowerCase();
  switch (status) {
    case "success":
      return analyticsChartColors.statusSuccess;
    case "warning":
      return analyticsChartColors.statusWarning;
    case "error":
      return analyticsChartColors.statusError;
    default:
      return ANALYTICS_CUSTOM_SERIES_COLORS[index % ANALYTICS_CUSTOM_SERIES_COLORS.length];
  }
};

const getTraceLatencySeriesColor = (series: CustomChartSeriesMeta, index: number) => {
  switch (series.key) {
    case "p50":
      return analyticsChartColors.latencyP50;
    case "p90":
      return analyticsChartColors.latencyP90;
    case "p95":
      return analyticsChartColors.latencyP95;
    case "p99":
      return analyticsChartColors.statusError;
    default:
      return ANALYTICS_CUSTOM_SERIES_COLORS[index % ANALYTICS_CUSTOM_SERIES_COLORS.length];
  }
};

const getCustomSeriesColor = (
  chart: CustomChartResponseItem,
  series: CustomChartSeriesMeta,
  index: number,
) => {
  switch (chart.id) {
    case "trace-status":
      return getTraceStatusSeriesColor(series, index);
    case "trace-latency":
      return getTraceLatencySeriesColor(series, index);
    default:
      return ANALYTICS_CUSTOM_SERIES_COLORS[index % ANALYTICS_CUSTOM_SERIES_COLORS.length];
  }
};

const getAxisTitleForUnit = (unit: string, context?: CustomChartOptionsContext) => {
  switch (unit) {
    case "count":
      return context?.countAxisTitle ?? "Requests";
    case "currency":
      return "Cost";
    case "duration_seconds":
      return "Latency";
    case "tokens":
      return "Tokens";
    default:
      return "Value";
  }
};

const buildTimeSeriesSelectionOptions = (
  isTimeSeries: boolean,
  dateEntries: DateRangeEntry[],
  context?: CustomChartOptionsContext,
) => {
  if (
    !isTimeSeries ||
    !context?.chartInterval ||
    !context.structuredDateRange ||
    !context.onDateRangeSelect
  ) {
    return { zoom: undefined, listeners: undefined };
  }

  return buildDateChartSelectionOptions({
    chartInterval: context.chartInterval,
    dateEntries,
    dateRange: context.structuredDateRange,
    onDateRangeSelect: context.onDateRangeSelect,
  });
};

const buildGroupedBucketDateRange = (
  dateEntries: DateRangeEntry[],
  structuredDateRange: { from: Date; to: Date },
  chartInterval: Parameters<typeof buildBucketAlignedRange>[2],
  dateKey: string,
) => {
  const dateIndex = dateEntries.findIndex(entry => entry.key === dateKey);
  if (dateIndex === -1) {
    return undefined;
  }

  return buildBucketAlignedRange(dateEntries, structuredDateRange, chartInterval, [
    dateIndex,
    dateIndex,
  ]);
};

const buildSingleSeriesBucketClickListeners = (
  isTimeSeries: boolean,
  dateEntries: DateRangeEntry[],
  context?: CustomChartOptionsContext,
) => {
  if (
    !isTimeSeries ||
    !context?.bucketClickToFilter ||
    !context.chartInterval ||
    !context.structuredDateRange ||
    !context.onDateRangeSelect
  ) {
    return undefined;
  }

  return {
    seriesNodeClick: (event: unknown) => {
      if (!isRecord(event) || !isRecord(event.datum)) {
        return;
      }

      const dateKey = event.datum.date;
      if (typeof dateKey !== "string") {
        return;
      }

      const dateIndex = dateEntries.findIndex(entry => entry.key === dateKey);
      if (dateIndex === -1) {
        return;
      }

      context.onDateRangeSelect?.(
        buildGroupedBucketDateRange(
          dateEntries,
          context.structuredDateRange!,
          context.chartInterval!,
          dateKey,
        ) ?? { from: new Date(), to: new Date() },
      );
    },
  };
};

const buildCategoryClickListeners = (context?: CustomChartOptionsContext) => {
  if (!context?.onCategoryClick) {
    return undefined;
  }

  return {
    seriesNodeClick: (event: unknown) => {
      if (!isRecord(event) || !isRecord(event.datum)) {
        return;
      }

      const label = event.datum.x ?? event.datum.tooltipTitle;
      if (typeof label !== "string") {
        return;
      }

      const { rawLabel } = event.datum;
      context.onCategoryClick?.({
        label,
        rawLabel: typeof rawLabel === "string" ? rawLabel : undefined,
      });
    },
  };
};

const buildGroupedTimeSeriesClickListeners = ({
  chart,
  isTimeSeries,
  isBarChart,
  dateEntries,
  context,
}: {
  chart: CustomChartResponseItem;
  isTimeSeries: boolean;
  isBarChart: boolean;
  dateEntries: DateRangeEntry[];
  context?: CustomChartOptionsContext;
}) => {
  if (
    !isTimeSeries ||
    !isBarChart ||
    !context?.onGroupedBucketClick ||
    !context.chartInterval ||
    !context.structuredDateRange
  ) {
    return undefined;
  }

  return {
    seriesNodeClick: (event: unknown) => {
      if (!isRecord(event) || !isRecord(event.datum) || typeof event.yKey !== "string") {
        return;
      }

      const dateKey = event.datum.date;
      if (typeof dateKey !== "string") {
        return;
      }

      const dateRange = buildGroupedBucketDateRange(
        dateEntries,
        context.structuredDateRange!,
        context.chartInterval!,
        dateKey,
      );
      if (!dateRange) {
        return;
      }

      const series = chart.series.find(item => item.key === event.yKey);
      const rawGroupValue = series?.rawLabel ?? event.yKey;
      const groupValue = series?.label ?? rawGroupValue;

      context.onGroupedBucketClick?.({
        dateRange,
        groupValue,
        rawGroupValue,
      });
    },
  };
};

const buildCartesianInteractionListeners = (
  chart: CustomChartResponseItem,
  isTimeSeries: boolean,
  dateEntries: DateRangeEntry[],
  context?: CustomChartOptionsContext,
) => {
  const isBarChart = chart.chartType === CustomChartType.Bar;
  const groupedListeners = buildGroupedTimeSeriesClickListeners({
    chart,
    isTimeSeries,
    isBarChart,
    dateEntries,
    context,
  });
  if (groupedListeners) {
    return groupedListeners;
  }

  const bucketListeners = buildSingleSeriesBucketClickListeners(isTimeSeries, dateEntries, context);
  if (bucketListeners) {
    return bucketListeners;
  }

  return buildCategoryClickListeners(context);
};

const buildMultiSeriesChartOptions = (
  chart: CustomChartResponseItem,
  context?: CustomChartOptionsContext,
): AgCartesianChartOptions => {
  const { isTimeSeries, plotData, dateEntries } = prepareMultiSeriesRows(chart, context);
  const bottomAxis = isTimeSeries ? buildBottomDateAxis(dateEntries) : bottomCategoryAxis;
  const xKey = isTimeSeries ? "date" : "x";
  const seriesDefinitions = chart.series.map((series, index) => ({
    ...series,
    color: getCustomSeriesColor(chart, series, index),
    formatValue: (value: number) => formatAxisValue(series.unit, value),
  }));
  const renderedSeriesDefinitions =
    chart.id === "trace-status" ? [...seriesDefinitions].reverse() : seriesDefinitions;

  const buildTooltipRows = (datum: Record<string, unknown>): AnalyticsTooltipRow[] => {
    const metricRows = seriesDefinitions
      .map((definition, index) => ({
        ...definition,
        index,
        value: Number(datum[definition.key] ?? 0),
      }))
      .sort((a, b) => b.value - a.value || a.index - b.index)
      .map(({ label, value, color, formatValue }) => ({
        label,
        value: formatValue(value),
        color,
      }));

    return [...metricRows, ...buildDerivedInsightRows(chart)];
  };

  const yKeys = seriesDefinitions.map(definition => definition.key);
  const axisGroups = seriesDefinitions.reduce<
    { unit: string; keys: string[]; position: "left" | "right" }[]
  >((groups, definition) => {
    const existingGroup = groups.find(group => group.unit === definition.unit);
    if (existingGroup) {
      existingGroup.keys.push(definition.key);
      return groups;
    }
    groups.push({
      unit: definition.unit,
      keys: [definition.key],
      position: groups.length === 0 ? "left" : "right",
    });
    return groups;
  }, []);
  const selectionOptions = buildTimeSeriesSelectionOptions(isTimeSeries, dateEntries, context);
  const interactionListeners = buildCartesianInteractionListeners(
    chart,
    isTimeSeries,
    dateEntries,
    context,
  );
  const hasInteractions = Boolean(interactionListeners);

  return {
    ...baseAnalyticsChartOptions,
    ...selectionOptions,
    listeners: {
      ...(selectionOptions.listeners ?? {}),
      ...(interactionListeners ?? {}),
    },
    legend: {
      enabled: true,
      item: {
        marker: {
          shape: "circle",
        },
      },
    },
    data: plotData,
    axes: [
      bottomAxis,
      ...(axisGroups.length > 1
        ? axisGroups.map(group =>
            numberAxis({
              keys: group.keys,
              position: group.position,
              title: getAxisTitleForUnit(group.unit, context),
              formatValue: value => formatAxisValue(group.unit, value),
            }),
          )
        : [
            numberAxis({
              keys: yKeys,
              title: getAxisTitleForUnit(axisGroups[0]?.unit ?? "number", context),
              formatValue: value => formatAxisValue(axisGroups[0]?.unit ?? "number", value),
            }),
          ]),
    ],
    series: renderedSeriesDefinitions.map((definition, index) => {
      const shouldRenderSharedTooltip =
        chart.id === "trace-status" ? index === renderedSeriesDefinitions.length - 1 : index === 0;
      const tooltip = {
        renderer: ({ datum }: { datum: Record<string, unknown> }) =>
          shouldRenderSharedTooltip
            ? createAnalyticsTooltipHtml({
                title: String(datum.dateLabel ?? datum.tooltipTitle ?? datum[xKey] ?? ""),
                rows: buildTooltipRows(datum),
              })
            : "",
      };

      return {
        ...buildCartesianSeries(chart.chartType, {
          color: definition.color,
          label: definition.label,
          tooltip,
          xKey,
          yKey: definition.key,
        }),
        ...(hasInteractions && chart.chartType === CustomChartType.Bar
          ? {
              stacked: Boolean(context?.onGroupedBucketClick),
              cursor: "pointer" as const,
              nodeClickRange: "nearest" as const,
            }
          : {}),
      };
    }),
  };
};

export const buildGenericCustomChartOptions = (
  chart: CustomChartResponseItem,
  context?: CustomChartOptionsContext,
): AgChartOptions => {
  switch (chart.chartType) {
    case CustomChartType.Pie:
    case CustomChartType.Donut:
      return buildPolarCustomChartOptions(chart, context);
    case CustomChartType.Histogram:
      return buildHistogramCustomChartOptions(chart);
    case CustomChartType.Heatmap:
      return buildHeatmapCustomChartOptions(chart);
    case CustomChartType.Treemap:
    case CustomChartType.Sunburst:
      return buildHierarchyCustomChartOptions(chart);
    case CustomChartType.Bar:
    case CustomChartType.Line:
    case CustomChartType.Area:
    default:
      break;
  }

  if (isMultiSeriesChart(chart)) {
    return buildMultiSeriesChartOptions(chart, context);
  }

  const unit = chart.series[0]?.unit ?? "number";
  const yTitle = chart.series[0]?.label ?? "Value";
  const { isTimeSeries, plotData, dateEntries } = prepareSingleSeriesRows(chart, context);
  const formatValue = (value: number) => formatAxisValue(unit, value);
  const bottomAxis = isTimeSeries ? buildBottomDateAxis(dateEntries) : bottomCategoryAxis;
  const xKey = isTimeSeries ? "date" : "x";
  const selectionOptions = buildTimeSeriesSelectionOptions(isTimeSeries, dateEntries, context);
  const interactionListeners = buildCartesianInteractionListeners(
    chart,
    isTimeSeries,
    dateEntries,
    context,
  );
  const hasInteractions = Boolean(interactionListeners);

  const tooltip = {
    renderer: ({ datum }: { datum: Record<string, unknown> }) =>
      createAnalyticsTooltipHtml({
        title: String(datum.dateLabel ?? datum.tooltipTitle ?? datum[xKey] ?? ""),
        rows: [
          {
            label: yTitle,
            value: formatValue(Number(datum.y)),
            color: ANALYTICS_CUSTOM_PRIMARY_COLOR,
          },
        ],
      }),
  };

  return {
    ...baseAnalyticsChartOptions,
    ...selectionOptions,
    listeners: {
      ...(selectionOptions.listeners ?? {}),
      ...(interactionListeners ?? {}),
    },
    legend: { enabled: false },
    data: plotData,
    axes: [bottomAxis, numberAxis({ keys: ["y"], title: yTitle, formatValue })],
    series: [
      {
        ...buildCartesianSeries(chart.chartType, {
          color: ANALYTICS_CUSTOM_PRIMARY_COLOR,
          label: yTitle,
          tooltip,
          xKey,
          yKey: "y",
        }),
        ...(hasInteractions
          ? {
              cursor: "pointer" as const,
              nodeClickRange: "nearest" as const,
            }
          : {}),
      },
    ],
  };
};
